import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useParams } from 'react-router';
import pick from 'lodash/pick';
import { ApplePayPaymentDetails } from 'src/integrations/PaymentProvider/ApplePayButton';
import { IPaymentProvider, paymentProviderFactory } from 'src/integrations/PaymentProvider/PaymentProvider';
import { IPaymentOptionValue } from 'components/bill/ui/ViewBill/Checkout/PaymentMethod/PaymentMethodDialog';
import {
    IAdjustmentLocalResource,
    IOrderCreatePaymentV8,
    IOrderPaymentReadResource,
    IOrderReadResourceV18,
    IUpdateOrderDetails,
    ILocalCouponAdjustment,
    OrderPaymentType,
    OrderScenario,
    OrderAdjustmentType
} from 'components/order/model/Order';
import { EOrderNonceKey, orderApi } from 'components/order/orderApi';
import { EPaymentProvider, isPaymentProvider } from 'components/settings/model/Settings';
import { setShouldUpdateGuestSession } from 'components/user/localAuth';
import { userApi } from 'components/user/userApi';
import { roundToDecimal } from 'lib/helpers';
import logger from 'lib/logger';
import { isDefined, isString } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { updateUser } from 'store/auth/authSlice';
import { resetBasket } from 'store/basket/basketSlice';
import { setLastRequest } from 'store/request/requestSlice';
import { ApplicationState } from 'store/store';
import { setPaySessionSplits } from '../model/helpers';
import { GooglePayPaymentDetails } from 'src/integrations/GooglePay';
import { generateOrderAdjustments, getSortedAdjustmentsForCheckout } from 'components/order/helpers';
import { useTippingSettings } from 'components/order/hooks/useTippingSettings';
import { useSnackbar } from 'notistack';

const getChallengeUrl = () =>
    `${process.env.ORDER_SERVICE_URL}/worldpay/3ds/redirectVerification?redirectUrl=${window.location.origin}${ROUTES.WORLDPAY}`;

export function usePayMyOrder(
    selectedPaymentOption: IPaymentOptionValue,
    orderDetails: IOrderReadResourceV18 | undefined,
    totalToPay: number,
    tipValue: number,
    selectedTip: number | undefined,
    handleAddNewCard: () => void,
    giftCardPayment?: IOrderCreatePaymentV8,
    selectedRewards?: IAdjustmentLocalResource[],
    coupons?: ILocalCouponAdjustment[],
    yourShares?: number,
    splitShares?: number,
    itemIds?: number[]
) {
    const { merchantId, locationId } = useParams<LocationRouteParams>();
    const tippingSettings = useTippingSettings(OrderScenario.TABLE);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const { push } = useLocalHistory();
    const { t } = useTranslation();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { lastRequest } = useSelector((state: ApplicationState) => state.request);
    const { user, isGuest } = useAuth();
    const [provider, setProvider] = React.useState<IPaymentProvider>();
    const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const tipToUse = React.useMemo(() => roundToDecimal(tipValue), [tipValue]);
    const adjustmentsToApply = React.useMemo(
        () =>
            getSortedAdjustmentsForCheckout(
                selectedRewards ?? [],
                (coupons ?? []).map(item => ({
                    type: OrderAdjustmentType.DISCOUNT,
                    couponCode: item.couponCode,
                    selectedOn: item.selectedOn
                }))
            ),
        [coupons, selectedRewards]
    );
    const adjustments = React.useMemo(
        () => generateOrderAdjustments(tipToUse, adjustmentsToApply),
        [adjustmentsToApply, tipToUse]
    );

    React.useEffect(() => {
        if (settings && !provider) {
            paymentProviderFactory.create(settings).then(paymentProvider => {
                setProvider(paymentProvider);
            });
        }
        return () => {
            if (provider && provider.reset) {
                provider.reset();
            }
        };
    }, [provider, provider?.reset, settings]);
    const handleToggleConfirmationDialog = React.useCallback(
        () => setIsConfirmationOpen(!isConfirmationOpen),
        [isConfirmationOpen]
    );
    const handlePlaceOrder = React.useCallback(
        (
                paymentType: OrderPaymentType,
                options?: {
                    auth?: { nonce?: string; threeDSecureData?: { verificationToken: string } };
                    wallet?:
                        | GooglePayPaymentDetails
                        | ApplePayPaymentDetails
                        | { worldpay: GooglePayPaymentDetails | ApplePayPaymentDetails };
                }
            ) =>
            () => {
                async function completeOrderPayment(paymentNonce: string | null) {
                    // Place order
                    try {
                        if (!orderDetails) {
                            throw new Error('No order details');
                        }
                        setIsSubmitting(true);
                        const paymentsToApply: IOrderCreatePaymentV8[] = [];
                        const payment: IOrderCreatePaymentV8 = {
                            type: paymentType,
                            amount: totalToPay
                        };
                        if (isDefined(yourShares)) {
                            payment.splits = yourShares;
                        }
                        if (Array.isArray(itemIds) && itemIds.length > 0) {
                            payment.itemIds = itemIds;
                        }
                        if (options?.auth) {
                            payment.auth = !!paymentNonce
                                ? { ...options.auth, nonce: paymentNonce }
                                : options.auth;
                        }
                        if (options?.wallet) {
                            payment.wallet = options.wallet;
                        }
                        if (!!provider && !payment?.auth) {
                            const deviceDataResult = await provider.collectDeviceData();

                            if (!!deviceDataResult) {
                                const { sessionId, transactionReference } = deviceDataResult;

                                if (isString(sessionId)) {
                                    payment.auth = {
                                        threeDSecureData: {
                                            deviceData: {
                                                collectionReference: sessionId,
                                                userAgentHeader: window.navigator.userAgent
                                            },
                                            challenge: {
                                                returnUrl: getChallengeUrl()
                                            },
                                            transactionReference
                                        }
                                    };
                                }
                            }
                        }
                        if (giftCardPayment) {
                            if (payment.amount !== 0) {
                                paymentsToApply.push(payment);
                            } else if (payment.itemIds) {
                                giftCardPayment.itemIds = payment.itemIds;
                            }
                            paymentsToApply.push(
                                pick(giftCardPayment, [
                                    'auth',
                                    'provider',
                                    'type',
                                    'amount',
                                    'itemIds'
                                ]) as IOrderPaymentReadResource
                            );
                        } else {
                            paymentsToApply.push(payment);
                        }

                        const orderBody: Partial<IUpdateOrderDetails> = {
                            adjustments,
                            payments: paymentsToApply
                        };
                        if (isDefined(yourShares) && splitShares) {
                            orderBody.splitNumber = splitShares + (orderDetails?.splitTotal?.splitsPaid ?? 0);
                        }
                        if (isDefined(selectedTip)) {
                            if (!!user && !!tippingSettings) {
                                userApi
                                    .updateUser(user._id, {
                                        defaultTip: {
                                            amount: selectedTip,
                                            scheme: tippingSettings.scheme
                                        }
                                    })
                                    .then(userResult => {
                                        dispatch(updateUser(userResult));
                                    })
                                    .catch(error => {
                                        logger.warn(error);
                                    });
                            }
                        }
                        setPaySessionSplits(undefined, merchantId, locationId, String(orderDetails.id));
                        const operation = await orderApi.updateOrder(orderDetails.id, orderBody);
                        setIsSubmitting(false);
                        if (isDefined(operation.id)) {
                            if (isGuest) {
                                if (merchantId) {
                                    setShouldUpdateGuestSession(merchantId);
                                }
                            }

                            push(
                                ROUTES.QUICKPAY.STATUS,
                                {
                                    operationId: operation.id.toString()
                                },
                                '',
                                { orderBodyToRecreatePat: { ...orderBody, id: orderDetails.id } }
                            );
                        }
                    } catch (err) {
                        setIsSubmitting(false);
                        if (!err) {
                            handleToggleConfirmationDialog();
                        } else {
                            enqueueSnackbar(t('CHECKOUT_ERROR_PLACING_ORDER'), { variant: 'error' });
                        }
                    }
                }

                completeOrderPayment(null);
            },
        [
            adjustments,
            dispatch,
            enqueueSnackbar,
            giftCardPayment,
            handleToggleConfirmationDialog,
            isGuest,
            itemIds,
            locationId,
            merchantId,
            orderDetails,
            provider,
            push,
            selectedTip,
            splitShares,
            t,
            tippingSettings,
            totalToPay,
            user,
            yourShares
        ]
    );
    const handlePayMyBillWithCard = React.useCallback(
        (forced?: boolean) => {
            if (selectedPaymentOption.card || forced) {
                if (
                    !forced &&
                    provider &&
                    settings &&
                    isPaymentProvider(settings.paymentProvider) &&
                    settings?.paymentProvider === EPaymentProvider.SQUARE &&
                    user &&
                    user?.card
                ) {
                    userApi.getCards(user.id).then(res => {
                        const userSelectedCard = res.cards.find(
                            c =>
                                selectedPaymentOption.card &&
                                selectedPaymentOption.card._id &&
                                c._id === selectedPaymentOption.card._id
                        );
                        if (userSelectedCard?.cardId) {
                            provider.threeDSecureAuthenticate(
                                {
                                    square: {
                                        cardId: userSelectedCard.cardId,
                                        intent: 'CHARGE',
                                        currency: settings.region.currencyCode,
                                        contact: {
                                            givenName: user?.firstName,
                                            familyName: user?.lastName
                                        },
                                        onVerificationEnd: (err, result) => {
                                            if (err || !result) {
                                                enqueueSnackbar(err?.message || 'Missing the result', {
                                                    variant: 'error'
                                                });
                                            } else {
                                                const createOrderFunction = handlePlaceOrder(
                                                    OrderPaymentType.CARD_ON_FILE,
                                                    {
                                                        auth: {
                                                            threeDSecureData: {
                                                                verificationToken: result.token
                                                            }
                                                        }
                                                    }
                                                );
                                                dispatch(setLastRequest(createOrderFunction));
                                                createOrderFunction();
                                            }
                                        }
                                    }
                                },
                                {
                                    payments: [
                                        {
                                            amount: totalToPay,
                                            type: OrderPaymentType.CARD_ON_FILE
                                        }
                                    ]
                                }
                            );
                        }
                    });
                } else {
                    const createOrderFunction = handlePlaceOrder(
                        // paymentCreation
                        OrderPaymentType.CARD_ON_FILE
                    );
                    dispatch(setLastRequest(createOrderFunction));
                    createOrderFunction();
                }
            } else {
                handleAddNewCard();
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            handleAddNewCard,
            handlePlaceOrder,
            provider,
            selectedPaymentOption.card,
            settings,
            totalToPay,
            user
        ]
    );
    const handleDigitalPayment = React.useCallback(
        (cardNonce: string, walletType: OrderPaymentType) => {
            const createOrderFunction = handlePlaceOrder(walletType, { auth: { nonce: cardNonce } });
            dispatch(setLastRequest(createOrderFunction));
            createOrderFunction();
        },
        [dispatch, handlePlaceOrder]
    );

    const handleJudoDigitalPayment = React.useCallback(
        (data: GooglePayPaymentDetails | ApplePayPaymentDetails, walletType: OrderPaymentType) => {
            const createOrderFunction = handlePlaceOrder(
                walletType,
                settings?.paymentProvider === EPaymentProvider.WORLDPAY
                    ? {
                          wallet: { worldpay: data }
                      }
                    : { wallet: data }
            );
            dispatch(setLastRequest(createOrderFunction));
            createOrderFunction();
        },
        [dispatch, handlePlaceOrder, settings?.paymentProvider]
    );
    const handleRetry = React.useCallback(() => {
        if (lastRequest) {
            lastRequest();
            handleToggleConfirmationDialog();
        }
    }, [handleToggleConfirmationDialog, lastRequest]);
    const handleErrorDialogClose = React.useCallback(() => {
        dispatch(resetBasket());
        orderApi.resetNonceByKey([EOrderNonceKey.UPDATE_ORDER]);
        push(ROUTES.QUICKPAY.CHECKOUT);
    }, [dispatch, push]);
    return {
        payWithCard: handlePayMyBillWithCard,
        isSubmitting,
        isConfirmationOpen,
        handleDigitalPayment,
        handleJudoDigitalPayment,
        onRetry: handleRetry,
        onErrorDialogClose: handleErrorDialogClose
    };
}
